import { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/authService';
import { toast } from 'react-hot-toast';

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action types
const AuthActionTypes = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_USER: 'SET_USER',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AuthActionTypes.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AuthActionTypes.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AuthActionTypes.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload.error,
      };

    case AuthActionTypes.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };

    case AuthActionTypes.SET_USER:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: !!action.payload.user,
        isLoading: false,
        error: null,
      };

    case AuthActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload.isLoading,
      };

    case AuthActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext(null);

// Provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('🔍 Initializing auth state...');
        console.log('🔍 authService.isAuthenticated():', authService.isAuthenticated());
        console.log('🔍 localStorage accessToken:', localStorage.getItem('accessToken'));
        console.log('🔍 localStorage user:', localStorage.getItem('user'));
        
        if (authService.isAuthenticated()) {
          const user = authService.getStoredUser();
          console.log('🔍 Stored user:', user);
          
          if (user) {
            console.log('✅ Setting user from localStorage');
            dispatch({
              type: AuthActionTypes.SET_USER,
              payload: { user },
            });
          } else {
            console.log('🔄 No stored user, fetching from server...');
            // Try to fetch current user from server
            const currentUser = await authService.getCurrentUser();
            console.log('✅ Fetched user from server:', currentUser);
            dispatch({
              type: AuthActionTypes.SET_USER,
              payload: { user: currentUser },
            });
          }
        } else {
          console.log('❌ Not authenticated, setting loading to false');
          dispatch({
            type: AuthActionTypes.SET_LOADING,
            payload: { isLoading: false },
          });
        }
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
        dispatch({
          type: AuthActionTypes.LOGOUT,
        });
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    console.log('🔍 Login attempt for:', email);
    dispatch({ type: AuthActionTypes.LOGIN_START });

    try {
      const { user } = await authService.login(email, password);
      console.log('✅ Login successful, user:', user);
      
      dispatch({
        type: AuthActionTypes.LOGIN_SUCCESS,
        payload: { user },
      });

      toast.success(`Welcome back, ${user.firstName}!`);
      return { success: true };
    } catch (error) {
      const errorMessage = error.message || 'Login failed';
      console.error('❌ Login failed:', errorMessage);
      
      dispatch({
        type: AuthActionTypes.LOGIN_FAILURE,
        payload: { error: errorMessage },
      });

      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
      dispatch({ type: AuthActionTypes.LOGOUT });
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if server logout fails
      dispatch({ type: AuthActionTypes.LOGOUT });
      toast.error('Logout failed, but you have been signed out locally');
    }
  };

  // Update user function
  const updateUser = (updatedUser) => {
    dispatch({
      type: AuthActionTypes.SET_USER,
      payload: { user: updatedUser },
    });
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: AuthActionTypes.CLEAR_ERROR });
  };

  // Forgot password function
  const forgotPassword = async (email) => {
    try {
      const message = await authService.forgotPassword(email);
      toast.success(message);
      return { success: true, message };
    } catch (error) {
      const errorMessage = error.message || 'Failed to send reset email';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Reset password function
  const resetPassword = async (token, password) => {
    try {
      const message = await authService.resetPassword(token, password);
      toast.success(message);
      return { success: true, message };
    } catch (error) {
      const errorMessage = error.message || 'Failed to reset password';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Check permissions function
  const hasPermission = (permission) => {
    if (!state.user || !state.user.Role || !state.user.Role.Permissions) {
      return false;
    }
    return state.user.Role.Permissions.some(p => p.name === permission);
  };

  // Check role function
  const hasRole = (role) => {
    if (!state.user || !state.user.Role) {
      return false;
    }
    return state.user.Role.name === role;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    if (!state.user || !state.user.Role) {
      return false;
    }
    return roles.includes(state.user.Role.name);
  };

  console.log('🔍 Current auth state:', {
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    user: state.user ? { id: state.user.id, email: state.user.email, role: state.user.Role?.name } : null
  });

  const value = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    loading: state.isLoading,
    error: state.error,
    login,
    logout,
    updateUser,
    clearError,
    forgotPassword,
    resetPassword,
    hasPermission,
    hasRole,
    hasAnyRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 